/** PSF kernel generation on the CPU (docs/OPTICS.md §4–5). One kernel set per (pane config, layer vergence, ppd). */

import { sceWeight } from './eye';
import {
  CHANNELS, LCA, WAVELENGTH_NM, accommodationAt, clamp, progressiveAddAt,
  type Channel, type FocalComponent, type PaneConfig,
} from './types';

/** Kernel radius limit per pyramid level, px. Drop to 12 on slow GPUs. */
export let MAXR = 16;
export function setMaxR(r: number): void {
  MAXR = r;
}

export const LEVELS = [1, 2, 4, 8, 16, 32, 64] as const;
const GOLDEN_ANGLE = 2.39996;
const DEG_PER_MRAD = 0.0572958;
const PROBE_RAYS = 400;
const MIN_RAYS = 3000;
const MAX_RAYS = 40000;

export interface KernelPart {
  /** Pyramid level (1, 2, 4, … 64). */
  L: number;
  /** Kernel radius in level pixels; grid is (2R+1)². */
  R: number;
  size: number;
  /** RGBA32F, row-major, +y up. A = G. */
  data: Float32Array;
}

interface Terms {
  Rp: number;
  resC: number;
  sinT: number;
  cosT: number;
  k4: number;
  kc: number;
  kch: number;
  kt: number;
  kts: number;
}

function makeTerms(cfg: PaneConfig): Terms {
  const theta = (cfg.axis * Math.PI) / 180;
  const h = cfg.hoa;
  return {
    Rp: cfg.pupil / 2,
    resC: cfg.resC,
    sinT: Math.sin(theta),
    cosT: Math.cos(theta),
    k4: (13.416 * h.c40) / 81,
    kc: (2.8284 * h.c31) / 27,
    kch: (2.8284 * h.c31h) / 27,
    kt: (2.8284 * h.c33) / 27,
    kts: (2.8284 * h.c33s) / 27,
  };
}

/** Ray deviation without the sphere-defocus term, mrad. Writes into out[0..1]. */
function baseDeviation(t: Terms, comp: FocalComponent, x: number, y: number, r: number, out: Float64Array): void {
  const u = -x * t.sinT + y * t.cosT;
  const r2 = x * x + y * y;
  const sa = t.k4 * (4 * r2 - 2 * t.Rp * t.Rp);
  const xy6 = 6 * x * y;
  let gx = t.resC * u * -t.sinT + sa * x + t.kc * xy6 + t.kch * (9 * x * x + 3 * y * y - 2 * t.Rp * t.Rp)
    + t.kt * (3 * x * x - 3 * y * y) + t.kts * xy6;
  let gy = t.resC * u * t.cosT + sa * y + t.kc * (3 * x * x + 9 * y * y - 2 * t.Rp * t.Rp) + t.kch * xy6
    + t.kt * -xy6 + t.kts * (3 * x * x - 3 * y * y);
  if (comp.profile) {
    const P = comp.profile(r);
    gx -= P * x;
    gy -= P * y;
  }
  out[0] = gx;
  out[1] = gy;
}

/** Perceptual attenuation of the per-channel chromatic term (DECISIONS D-006; 0.4 → 0.2 with HDR night lights). */
export const CHROMA_SCALE = 0.2;

/** Per-channel sphere defocus of a component at layer vergence V, D (OPTICS §3). */
function channelDefocus(cfg: PaneConfig, comp: FocalComponent, V: number): Record<Channel, number> {
  const ds = V + cfg.resS - progressiveAddAt(cfg, V) - accommodationAt(cfg, V) - comp.add;
  const out = { r: 0, g: 0, b: 0 };
  for (const c of CHANNELS) {
    const chroma = comp.diffractive ? comp.add * (WAVELENGTH_NM[c] / 550 - 1) : 0;
    out[c] = ds + CHROMA_SCALE * (LCA[c] - chroma);
  }
  return out;
}

function inZone(comp: FocalComponent, r: number): boolean {
  if (comp.r0 === undefined && comp.r1 === undefined) return true;
  return r >= (comp.r0 ?? 0) && r < (comp.r1 ?? Infinity);
}

interface PlannedComponent {
  comp: FocalComponent;
  delta: Record<Channel, number>;
  /** Max deviation extent, level-1 px. */
  extentPx: number;
  L: number;
  rays: number;
}

function planComponent(cfg: PaneConfig, t: Terms, comp: FocalComponent, V: number, pxPerMrad: number): PlannedComponent {
  const delta = channelDefocus(cfg, comp, V);
  const dev = new Float64Array(2);
  let maxDev = 0;
  for (let i = 0; i < PROBE_RAYS; i++) {
    const r = t.Rp * Math.sqrt((i + 0.5) / PROBE_RAYS);
    if (!inZone(comp, r)) continue;
    const phi = i * GOLDEN_ANGLE;
    const x = r * Math.cos(phi);
    const y = r * Math.sin(phi);
    baseDeviation(t, comp, x, y, r, dev);
    for (const c of CHANNELS) {
      const gx = dev[0]! + delta[c] * x;
      const gy = dev[1]! + delta[c] * y;
      const m = Math.max(Math.abs(gx), Math.abs(gy));
      if (m > maxDev) maxDev = m;
    }
  }
  const extentPx = maxDev * pxPerMrad;
  let L = 64;
  for (const cand of LEVELS) {
    if (extentPx / cand <= MAXR) {
      L = cand;
      break;
    }
  }
  const RL = extentPx / L;
  const rays = Math.round(clamp(10 * Math.PI * RL * RL, MIN_RAYS, MAX_RAYS));
  return { comp, delta, extentPx, L, rays };
}

function splat(grid: Float32Array, size: number, fx: number, fy: number, w: number): void {
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const tx = fx - x0;
  const ty = fy - y0;
  const last = size - 1;
  const xa = clamp(x0, 0, last);
  const xb = clamp(x0 + 1, 0, last);
  const ya = clamp(y0, 0, last);
  const yb = clamp(y0 + 1, 0, last);
  const i00 = ya * size + xa;
  const i10 = ya * size + xb;
  const i01 = yb * size + xa;
  const i11 = yb * size + xb;
  grid[i00] = grid[i00]! + w * (1 - tx) * (1 - ty);
  grid[i10] = grid[i10]! + w * tx * (1 - ty);
  grid[i01] = grid[i01]! + w * (1 - tx) * ty;
  grid[i11] = grid[i11]! + w * tx * ty;
}

function gaussianBlur(grid: Float32Array, size: number, sigma: number): void {
  const radius = Math.ceil(3 * sigma);
  if (radius < 1) return;
  const weights = new Float32Array(2 * radius + 1);
  let sum = 0;
  for (let i = -radius; i <= radius; i++) {
    const w = Math.exp(-(i * i) / (2 * sigma * sigma));
    weights[i + radius] = w;
    sum += w;
  }
  for (let i = 0; i < weights.length; i++) weights[i]! /= sum;

  const tmp = new Float32Array(size * size);
  // Horizontal pass.
  for (let y = 0; y < size; y++) {
    const row = y * size;
    for (let x = 0; x < size; x++) {
      let acc = 0;
      for (let k = -radius; k <= radius; k++) {
        const xx = x + k;
        if (xx < 0 || xx >= size) continue;
        acc += grid[row + xx]! * weights[k + radius]!;
      }
      tmp[row + x] = acc;
    }
  }
  // Vertical pass.
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      let acc = 0;
      for (let k = -radius; k <= radius; k++) {
        const yy = y + k;
        if (yy < 0 || yy >= size) continue;
        acc += tmp[yy * size + x]! * weights[k + radius]!;
      }
      grid[y * size + x] = acc;
    }
  }
}

/** Diffraction smoothing σ in level pixels for a channel (OPTICS §5.5). */
function diffractionSigma(cfg: PaneConfig, c: Channel, pxPerMrad: number, L: number): number {
  const sigmaMrad = (0.42 * WAVELENGTH_NM[c] * 1e-3) / cfg.pupil;
  return Math.max(0.5, (sigmaMrad * pxPerMrad) / L);
}

/** Build the kernel parts for one pane config and one depth layer. Uncached. */
export function buildKernel(cfg: PaneConfig, V: number, ppd: number): KernelPart[] {
  const t = makeTerms(cfg);
  const pxPerMrad = ppd * DEG_PER_MRAD;
  const planned = cfg.components.map((comp) => planComponent(cfg, t, comp, V, pxPerMrad));

  const groups = new Map<number, PlannedComponent[]>();
  for (const p of planned) {
    const g = groups.get(p.L);
    if (g) g.push(p);
    else groups.set(p.L, [p]);
  }

  const parts: { part: KernelPart; grids: Record<Channel, Float32Array> }[] = [];
  const dev = new Float64Array(2);

  for (const [L, comps] of [...groups.entries()].sort((a, b) => a[0] - b[0])) {
    const sigma = { r: 0, g: 0, b: 0 } as Record<Channel, number>;
    let sigmaMax = 0;
    for (const c of CHANNELS) {
      sigma[c] = diffractionSigma(cfg, c, pxPerMrad, L);
      sigmaMax = Math.max(sigmaMax, sigma[c]);
    }
    const RL = Math.max(...comps.map((p) => p.extentPx / L));
    const R = Math.ceil(RL) + Math.ceil(3 * sigmaMax) + 1;
    const size = 2 * R + 1;
    const grids: Record<Channel, Float32Array> = {
      r: new Float32Array(size * size),
      g: new Float32Array(size * size),
      b: new Float32Array(size * size),
    };
    const scale = pxPerMrad / L;

    for (const p of comps) {
      const { comp, delta, rays } = p;
      // Normalise per component over the whole spiral (in-zone or not), so that components with
      // different ray counts keep the energy ratio of their weights and zonal parts get the
      // Stiles–Crawford-weighted zone fraction.
      let sceTotal = 0;
      for (let i = 0; i < rays; i++) sceTotal += sceWeight(t.Rp * Math.sqrt((i + 0.5) / rays));
      const wScale = comp.w / sceTotal;
      for (let i = 0; i < rays; i++) {
        const r = t.Rp * Math.sqrt((i + 0.5) / rays);
        if (!inZone(comp, r)) continue;
        const phi = i * GOLDEN_ANGLE;
        const x = r * Math.cos(phi);
        const y = r * Math.sin(phi);
        const w = sceWeight(r) * wScale;
        baseDeviation(t, comp, x, y, r, dev);
        for (const c of CHANNELS) {
          const gx = dev[0]! + delta[c] * x;
          const gy = dev[1]! + delta[c] * y;
          splat(grids[c], size, R + gx * scale, R + gy * scale, w);
        }
      }
    }
    for (const c of CHANNELS) gaussianBlur(grids[c], size, sigma[c]);
    parts.push({ part: { L, R, size, data: new Float32Array(size * size * 4) }, grids });
  }

  // ICL ring halo (OPTICS §5.8): a thin ring at 1.2° carrying a small share of the energy.
  const ringEnergy = cfg.iclRing ? ICL_RING_ENERGY * (t.Rp / 3) ** 2 : 0;

  // Normalize each channel over all parts to (1 − ring energy), then pack RGBA with A = G.
  const totals = { r: 0, g: 0, b: 0 } as Record<Channel, number>;
  for (const { grids } of parts) {
    for (const c of CHANNELS) {
      let s = 0;
      const g = grids[c];
      for (let i = 0; i < g.length; i++) s += g[i]!;
      totals[c] += s;
    }
  }
  for (const { part, grids } of parts) {
    const n = part.size * part.size;
    const d = part.data;
    const inv = { r: (1 - ringEnergy) / totals.r, g: (1 - ringEnergy) / totals.g, b: (1 - ringEnergy) / totals.b };
    for (let i = 0; i < n; i++) {
      const gv = grids.g[i]! * inv.g;
      d[i * 4] = grids.r[i]! * inv.r;
      d[i * 4 + 1] = gv;
      d[i * 4 + 2] = grids.b[i]! * inv.b;
      d[i * 4 + 3] = gv;
    }
  }
  const out = parts.map((p) => p.part);
  if (ringEnergy > 0) out.push(ringPart(ringEnergy, ppd));
  return out;
}

const ICL_RING_ENERGY = 0.012;
const ICL_RING_RADIUS_DEG = 1.2;
const ICL_RING_SIGMA_DEG = 0.08;

/** Gaussian ring part (all channels equal) normalised to `energy`. */
function ringPart(energy: number, ppd: number): KernelPart {
  const r0 = ICL_RING_RADIUS_DEG * ppd;
  const sigma = ICL_RING_SIGMA_DEG * ppd;
  const extent = r0 + 3 * sigma;
  let L = 64;
  for (const cand of LEVELS) {
    if (extent / cand <= MAXR) {
      L = cand;
      break;
    }
  }
  const R = Math.ceil(extent / L) + 1;
  const size = 2 * R + 1;
  const data = new Float32Array(size * size * 4);
  let sum = 0;
  const vals = new Float32Array(size * size);
  for (let j = 0; j < size; j++) {
    for (let i = 0; i < size; i++) {
      const d = Math.hypot(i - R, j - R) * L;
      const v = Math.exp(-((d - r0) ** 2) / (2 * sigma * sigma));
      vals[j * size + i] = v;
      sum += v;
    }
  }
  const k = energy / sum;
  for (let i = 0; i < vals.length; i++) {
    const v = vals[i]! * k;
    data[i * 4] = v;
    data[i * 4 + 1] = v;
    data[i * 4 + 2] = v;
    data[i * 4 + 3] = v;
  }
  return { L, R, size, data };
}

/** Sum of one channel over all parts (should be 1 after normalization). */
export function kernelEnergy(parts: KernelPart[], channel: 0 | 1 | 2 | 3): number {
  let s = 0;
  for (const p of parts) {
    for (let i = channel; i < p.data.length; i += 4) s += p.data[i]!;
  }
  return s;
}

const cache = new Map<string, KernelPart[]>();
const CACHE_LIMIT = 96;

export function kernelKey(cfg: PaneConfig, V: number, ppd: number): string {
  return `${cfg.key}|${V.toFixed(4)}|${ppd.toFixed(3)}|${MAXR}`;
}

/** Cached kernel parts. */
export function getKernel(cfg: PaneConfig, V: number, ppd: number): KernelPart[] {
  const key = kernelKey(cfg, V, ppd);
  const hit = cache.get(key);
  if (hit) return hit;
  const parts = buildKernel(cfg, V, ppd);
  if (cache.size >= CACHE_LIMIT) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
  cache.set(key, parts);
  return parts;
}
